import { keepPreviousData, queryOptions } from "@tanstack/react-query"

import { getIsomorphicSupabaseClient } from "@repo/supabase/isomorphic-client"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import { createResourceInvoicesService } from "./resource-invoices.service"

export const getExtendedResourceInvoicesQueryOptions = ({
  resourceUnitId,
  filters,
}: {
  resourceUnitId?: string
  filters?: DataTableBaseSearch
} = {}) =>
  queryOptions({
    queryKey:
      !resourceUnitId && !filters
        ? ["extended-resource-invoices"]
        : ["extended-resource-invoices", "list", resourceUnitId, filters],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const resourceInvoicesService = createResourceInvoicesService(supabase)
      return resourceInvoicesService.getManyExtended({
        resourceUnitId,
        filters,
      })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })

export const getExtendedResourceInvoiceQueryOptions = ({
  invoiceId,
}: {
  invoiceId: string
}) =>
  queryOptions({
    queryKey: ["extended-resource-invoices", invoiceId],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const resourceInvoicesService = createResourceInvoicesService(supabase)
      return resourceInvoicesService.getOneExtended({ invoiceId })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })

export const getResourceInvoicesForExportQueryOptions = ({
  invoicePeriodId,
  businessEntityUnitId,
}: {
  invoicePeriodId: string
  businessEntityUnitId?: string
}) =>
  queryOptions({
    queryKey: [
      "resource-invoices-export",
      invoicePeriodId,
      businessEntityUnitId,
    ],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const resourceInvoicesService = createResourceInvoicesService(supabase)
      return resourceInvoicesService.getManyExtended({
        filters: {
          page: 1,
          page_size: 1000,
          columnFilters: [
            { id: "invoice_period_id", value: invoicePeriodId },
            ...(businessEntityUnitId
              ? [
                  {
                    id: "business_entity_unit_ids",
                    value: [businessEntityUnitId],
                  },
                ]
              : []),
          ],
        },
      })
    },
    staleTime: 0, // Don't cache export queries
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
